import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { withTimeout } from "@/hooks/useLoadState";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Upload, DollarSign, RefreshCw, Loader2, Plus, School as SchoolIcon, Users as UsersIcon, GitMerge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;
const buildHeaders = (session) => ({
  "x-tenant-id": session?.tenant?.id || "tenant_1",
  "x-user-id": session?.user?.id || "admin",
  "x-user-role": session?.user?.role || "admin",
});
const fmt = (n) => "$" + Number(n || 0).toLocaleString();
const ALL = "__all__";

const TABS = [
  { key: "directory", label: "Directory", icon: SchoolIcon },
  { key: "authorizers", label: "Authorizers", icon: UsersIcon },
  { key: "import", label: "Import", icon: Upload },
  { key: "pricing", label: "Pricing", icon: DollarSign },
];

export default function SchoolsAdminPage({ session }) {
  const headers = useMemo(() => buildHeaders(session), [session]);
  const [tab, setTab] = useState("directory");

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6" data-testid="schools-admin">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[11.5px] font-semibold tracking-[0.14em] uppercase text-[#9aa2b1]">
          Super Admin · Internal
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#111827] tracking-tight">School Directory</h1>
        <p className="text-sm text-[#6b7280] mt-1 max-w-2xl">
          Master directory of US K-12 schools used for Authorizer onboarding. Internal 589 Labs view only.
        </p>
      </div>

      <div className="flex gap-1 border-b border-[#e5e7eb] mb-6 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`schools-tab-${t.key}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                active ? "border-[#b91c1c] text-[#b91c1c]" : "border-transparent text-[#6b7280] hover:text-[#111827]"
              }`}
            >
              <Icon className="h-4 w-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "directory" && <DirectoryTab headers={headers} />}
      {tab === "authorizers" && <AuthorizersTab headers={headers} />}
      {tab === "import" && <ImportTab headers={headers} />}
      {tab === "pricing" && <PricingTab headers={headers} />}
    </div>
  );
}

/* ─────────────────────────── Authorizers ─────────────────────────── */
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

function AuthorizersTab({ headers }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await withTimeout(axios.get(`${API}/api/admin/authorizers`, { headers })); setRows(r.data.authorizers || []); }
    catch { toast.error("Failed to load authorizers (request may have timed out) — use Refresh to retry."); }
    setLoading(false);
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const setState = async (email, st) => {
    setSaving(email);
    try {
      await axios.put(`${API}/api/admin/authorizers/assigned-state`,
        { user_email: email, assigned_state: st },
        { headers: { ...headers, "Content-Type": "application/json" } });
      setRows((p) => p.map((r) => r.user_email === email ? { ...r, assigned_state: st } : r));
      toast.success(`Assigned ${st} to ${email}`);
    } catch { toast.error("Failed to set state"); }
    setSaving("");
  };

  return (
    <div data-testid="schools-authorizers">
      <p className="text-sm text-[#6b7280] mb-4">Set each Authorizer's assigned State. Authorizers can only select schools within their assigned State during onboarding.</p>
      <div className="border border-[#e5e7eb] rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-[#f9fafb] text-[#6b7280] text-xs uppercase">
            <tr><th className="text-left px-4 py-3">Authorizer</th><th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Portfolios</th><th className="text-left px-4 py-3">Assigned State</th></tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.user_email} className="border-t border-[#f1f2f4]" data-testid={`authorizer-row-${a.user_email}`}>
                <td className="px-4 py-3 font-medium text-[#111827]">{a.name || "—"}</td>
                <td className="px-4 py-3 text-[#374151]">{a.user_email}</td>
                <td className="px-4 py-3 text-[#6b7280]">{a.portfolio_count}</td>
                <td className="px-4 py-3">
                  <select data-testid={`authorizer-state-${a.user_email}`} value={a.assigned_state || ""}
                    disabled={saving === a.user_email}
                    onChange={(e) => setState(a.user_email, e.target.value)}
                    className="h-9 rounded-md border border-slate-200 px-2 text-sm bg-white">
                    <option value="">— none —</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-[#9ca3af]">No authorizers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────────────── Directory ─────────────────────────── */
function DirectoryTab({ headers }) {
  const [facets, setFacets] = useState({ states: [], school_types: [], operational_statuses: [], total: 0 });
  const [filters, setFilters] = useState({ q: "", state: "", school_type: "", operational_status: "", assigned: "" });
  const [data, setData] = useState({ schools: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const debTimer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/admin/schools/facets`, { headers }).then((r) => setFacets(r.data)).catch(() => {});
  }, [headers]);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, page_size: 25 };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await axios.get(`${API}/api/admin/schools`, { headers, params });
      setData(r.data); setPage(r.data.page);
    } catch { toast.error("Failed to load schools"); }
    setLoading(false);
  }, [headers, filters]);

  useEffect(() => {
    if (debTimer.current) clearTimeout(debTimer.current);
    debTimer.current = setTimeout(() => load(1), 300);
    return () => clearTimeout(debTimer.current);
  }, [filters, load]);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v === ALL ? "" : v }));

  return (
    <div data-testid="schools-directory">
      <div className="flex flex-col lg:flex-row lg:items-end gap-3 mb-4">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9ca3af]" />
          <Input
            data-testid="schools-search"
            placeholder="Search name, NCES ID, city or ZIP…"
            value={filters.q}
            onChange={(e) => setF("q", e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Select value={filters.state || ALL} onValueChange={(v) => setF("state", v)}>
            <SelectTrigger data-testid="schools-filter-state" className="h-10"><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All States</SelectItem>
              {facets.states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.school_type || ALL} onValueChange={(v) => setF("school_type", v)}>
            <SelectTrigger data-testid="schools-filter-type" className="h-10"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All Types</SelectItem>
              {facets.school_types.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.operational_status || ALL} onValueChange={(v) => setF("operational_status", v)}>
            <SelectTrigger data-testid="schools-filter-status" className="h-10"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All Statuses</SelectItem>
              {facets.operational_statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.assigned || ALL} onValueChange={(v) => setF("assigned", v)}>
            <SelectTrigger data-testid="schools-filter-assigned" className="h-10"><SelectValue placeholder="Assignment" /></SelectTrigger>
            <SelectContent><SelectItem value={ALL}>All</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem></SelectContent>
          </Select>
        </div>
        <Button data-testid="schools-add-btn" onClick={() => setAddOpen(true)} className="bg-[#b91c1c] hover:bg-[#991b1b] shrink-0">
          <Plus className="h-4 w-4 mr-1" /> Add School
        </Button>
      </div>

      <div className="text-xs text-[#6b7280] mb-2" data-testid="schools-result-count">
        {loading ? "Loading…" : `${data.total.toLocaleString()} schools · ${facets.total.toLocaleString()} in directory`}
      </div>

      <div className="border border-[#e5e7eb] rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#f9fafb] text-[#6b7280] text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left font-semibold px-4 py-3">School</th>
              <th className="text-left font-semibold px-4 py-3">City / State</th>
              <th className="text-left font-semibold px-4 py-3">County</th>
              <th className="text-left font-semibold px-4 py-3">Type</th>
              <th className="text-left font-semibold px-4 py-3">NCES ID</th>
              <th className="text-left font-semibold px-4 py-3">Status</th>
              <th className="text-left font-semibold px-4 py-3">Assignment</th>
            </tr>
          </thead>
          <tbody>
            {data.schools.map((s) => (
              <tr key={s.id} className="border-t border-[#f1f2f4] hover:bg-[#fafafa]" data-testid={`school-row-${s.id}`}>
                <td className="px-4 py-3 font-medium text-[#111827]">{s.school_name}
                  {s.is_active === false && <span className="ml-2 text-[11.5px] px-1.5 py-0.5 rounded bg-[#f3f4f6] text-[#6b7280]">Inactive</span>}
                </td>
                <td className="px-4 py-3 text-[#374151]">{s.city}, {s.state}</td>
                <td className="px-4 py-3 text-[#6b7280]">{s.county || "—"}</td>
                <td className="px-4 py-3 text-[#6b7280]">{s.school_type || "—"}</td>
                <td className="px-4 py-3 text-[#6b7280] font-mono text-xs">{s.nces_school_id || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${s.operational_status === "Open" ? "bg-[#dcfce7] text-[#166534]" : "bg-[#fee2e2] text-[#991b1b]"}`}>
                    {s.operational_status || "—"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {s.assigned_portfolio_id
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e]">Assigned</span>
                    : <span className="text-xs text-[#9ca3af]">Available</span>}
                </td>
              </tr>
            ))}
            {!loading && data.schools.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#9ca3af]">No schools match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <Button variant="outline" size="sm" disabled={page <= 1 || loading}
          data-testid="schools-prev-page" onClick={() => load(page - 1)}>Previous</Button>
        <span className="text-sm text-[#6b7280]">Page {data.page} of {data.pages}</span>
        <Button variant="outline" size="sm" disabled={page >= data.pages || loading}
          data-testid="schools-next-page" onClick={() => load(page + 1)}>Next</Button>
      </div>

      <AddSchoolModal open={addOpen} onClose={() => setAddOpen(false)} headers={headers}
        onAdded={() => { setAddOpen(false); load(1); }} />
    </div>
  );
}

function AddSchoolModal({ open, onClose, headers, onAdded }) {
  const [form, setForm] = useState({ school_name: "", state: "", city: "", school_type: "", nces_school_id: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!form.school_name.trim() || !form.state.trim()) { toast.error("School name and state are required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/admin/schools`, form, { headers: { ...headers, "Content-Type": "application/json" } });
      toast.success("School added"); onAdded();
      setForm({ school_name: "", state: "", city: "", school_type: "", nces_school_id: "" });
    } catch { toast.error("Failed to add school"); }
    setSaving(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[480px]" data-testid="school-add-modal">
        <DialogHeader><DialogTitle>Add School</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="add-school-name" placeholder="School name *" value={form.school_name} onChange={(e) => set("school_name", e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="add-school-state" placeholder="State * (e.g. NJ)" value={form.state} onChange={(e) => set("state", e.target.value)} />
            <Input data-testid="add-school-city" placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)} />
          </div>
          <Input data-testid="add-school-type" placeholder="School type" value={form.school_type} onChange={(e) => set("school_type", e.target.value)} />
          <Input data-testid="add-school-nces" placeholder="NCES School ID" value={form.nces_school_id} onChange={(e) => set("nces_school_id", e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="add-school-submit" className="bg-[#b91c1c] hover:bg-[#991b1b]" disabled={saving} onClick={submit}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add School"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────── Import ─────────────────────────── */
function ImportTab({ headers }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [sourceYear, setSourceYear] = useState(String(new Date().getFullYear()));
  const pollRef = useRef(null);

  const loadJobs = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/admin/schools/import/jobs`, { headers }); setJobs(r.data.jobs || []); }
    catch {}
  }, [headers]);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => {
    const running = jobs.some((j) => ["queued", "running"].includes(j.status));
    if (running) { pollRef.current = setInterval(loadJobs, 1500); return () => clearInterval(pollRef.current); }
  }, [jobs, loadJobs]);

  const [dupes, setDupes] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);
  const [dedupeJob, setDedupeJob] = useState(null);
  const dedupePoll = useRef(null);
  const scanDupes = useCallback(async () => {
    setDupBusy(true);
    try { const r = await axios.get(`${API}/api/admin/schools/duplicates/scan`, { headers }); setDupes(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Duplicate scan failed"); }
    setDupBusy(false);
  }, [headers]);
  const pollDedupe = useCallback((jobId) => {
    if (dedupePoll.current) clearInterval(dedupePoll.current);
    dedupePoll.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/admin/schools/duplicates/reprocess/status?job_id=${jobId}`, { headers });
        setDedupeJob(r.data);
        if (["completed", "failed"].includes(r.data.status)) {
          clearInterval(dedupePoll.current);
          if (r.data.status === "completed") toast.success(`Deduplication complete · ${r.data.merged_groups} auto-merged · ${r.data.queued_groups} queued for review`);
          scanDupes();
        }
      } catch { /* keep polling */ }
    }, 2000);
  }, [headers, scanDupes]);
  const runDedupe = async () => {
    if (!window.confirm("Run background deduplication?\n\nHigh-confidence matches (same NCES ID or exact name+address) are auto-merged. Everything else is queued for your review. Assignments, portfolios, licenses and diagnostics are preserved.")) return;
    setDupBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/schools/duplicates/reprocess`, {}, { headers });
      toast.success("Deduplication started in background");
      setDedupeJob({ status: "queued" });
      pollDedupe(r.data.job_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start deduplication"); }
    setDupBusy(false);
  };
  useEffect(() => () => dedupePoll.current && clearInterval(dedupePoll.current), []);

  const doPreview = async (f) => {
    setFile(f); setPreview(null);
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/admin/schools/import/preview`, fd, { headers });
      setPreview(r.data); setMapping(r.data.auto_map || {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    setBusy(false);
  };

  const commit = async () => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mapping", JSON.stringify(mapping));
    fd.append("import_source", "admin_upload");
    fd.append("source_year", sourceYear);
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/schools/import/commit`, fd, { headers });
      toast.success("Import started in background");
      setPreview(null); setFile(null); loadJobs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed to start"); }
    setBusy(false);
  };

  const FIELDS = preview?.master_fields || [];
  const mapCount = Object.values(mapping).filter(Boolean).length;

  return (
    <div className="space-y-6" data-testid="schools-import">
      <div className="border-2 border-dashed border-[#e5e7eb] rounded-lg p-8 text-center bg-[#fafafa]">
        <Upload className="h-8 w-8 mx-auto text-[#9ca3af] mb-2" />
        <p className="text-sm text-[#374151] font-medium">Upload a CSV or XLSX school file</p>
        <p className="text-xs text-[#6b7280] mb-4">Handles 100,000+ rows. Import runs in the background.</p>
        <input id="school-import-file" data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls"
          className="hidden" onChange={(e) => e.target.files[0] && doPreview(e.target.files[0])} />
        <label htmlFor="school-import-file">
          <Button asChild className="bg-[#b91c1c] hover:bg-[#991b1b]"><span>{busy && !preview ? "Reading…" : "Choose File"}</span></Button>
        </label>
        {file && <div className="text-xs text-[#6b7280] mt-3">{file.name}</div>}
      </div>

      <div className="border border-[#e5e7eb] rounded-lg p-4 sm:p-5 bg-white" data-testid="dedup-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold text-[#111827]">Deduplicate schools</h3>
            <p className="text-xs text-[#6b7280] max-w-xl">Detects duplicates by NCES ID, State ID and normalized name / address / city / state (with malformed-ID detection). High-confidence matches auto-merge; the rest are queued for your review. Assignments, portfolios, licenses and diagnostics are preserved.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={scanDupes} disabled={dupBusy} data-testid="dedup-scan-btn">
              {dupBusy && !dedupeJob ? <Loader2 className="h-4 w-4 animate-spin" /> : "Scan"}
            </Button>
            <Button className="bg-[#b91c1c] hover:bg-[#991b1b]" onClick={runDedupe} disabled={dupBusy || (dedupeJob && ["queued", "running"].includes(dedupeJob.status))} data-testid="dedup-run-btn">
              {dedupeJob && ["queued", "running"].includes(dedupeJob.status) ? <><Loader2 className="h-4 w-4 animate-spin mr-1" />Running…</> : "Run Deduplication"}
            </Button>
            <Button asChild variant="outline" data-testid="dedup-review-link">
              <Link to="/app/school-duplicates"><GitMerge className="h-4 w-4 mr-1" />Review Queue</Link>
            </Button>
          </div>
        </div>
        {dedupeJob && ["queued", "running"].includes(dedupeJob.status) && (
          <div className="mt-3 text-sm text-[#b45309]" data-testid="dedup-progress">
            Processing groups: <b>{dedupeJob.processed_groups || 0}</b>/{dedupeJob.total_groups ?? "…"} · auto-merged <b>{dedupeJob.merged_groups || 0}</b> · queued <b>{dedupeJob.queued_groups || 0}</b>
          </div>
        )}
        {dupes && (
          <div className="mt-3 text-sm text-[#374151]" data-testid="dedup-results">
            <b>{dupes.duplicate_groups.toLocaleString()}</b> duplicate groups · <b className="text-emerald-700">{(dupes.auto_mergeable_groups || 0).toLocaleString()}</b> auto-mergeable · <b className="text-amber-700">{(dupes.needs_review_groups || 0).toLocaleString()}</b> need review · <b>{(dupes.total_duplicate_records || 0).toLocaleString()}</b> extra records
            {dupes.duplicate_groups === 0 && <span className="text-emerald-700 ml-2">✓ No duplicates found.</span>}
          </div>
        )}
      </div>

      {preview && (
        <div className="border border-[#e5e7eb] rounded-lg p-4 sm:p-6 bg-white" data-testid="import-mapping">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-semibold text-[#111827]">Map columns</h3>
              <p className="text-xs text-[#6b7280]">{preview.total_rows.toLocaleString()} rows · {mapCount} columns mapped</p>
            </div>
            <div className="flex items-center gap-2">
              <Input className="w-28 h-9" value={sourceYear} onChange={(e) => setSourceYear(e.target.value)} placeholder="Source year" data-testid="import-source-year" />
              <Button data-testid="import-commit-btn" className="bg-[#111827] hover:bg-black" disabled={busy} onClick={commit}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start Import"}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[280px] overflow-y-auto">
            {preview.headers.map((h) => (
              <div key={h} className="flex items-center gap-2">
                <span className="text-xs font-mono text-[#374151] truncate flex-1" title={h}>{h}</span>
                <Select value={mapping[h] || ALL} onValueChange={(v) => setMapping((p) => ({ ...p, [h]: v === ALL ? "" : v }))}>
                  <SelectTrigger className="h-8 w-40 text-xs" data-testid={`import-map-${h}`}><SelectValue placeholder="— skip —" /></SelectTrigger>
                  <SelectContent><SelectItem value={ALL}>— skip —</SelectItem>
                    {FIELDS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-[#111827] text-sm">Recent imports</h3>
          <Button variant="ghost" size="sm" onClick={loadJobs} data-testid="import-refresh-jobs"><RefreshCw className="h-4 w-4" /></Button>
        </div>
        <div className="border border-[#e5e7eb] rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-[#f9fafb] text-[#6b7280] text-xs uppercase">
              <tr><th className="text-left px-4 py-2">File</th><th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Progress</th><th className="text-left px-4 py-2">Inserted</th>
                <th className="text-left px-4 py-2">Updated</th><th className="text-left px-4 py-2">Errors</th></tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} className="border-t border-[#f1f2f4]" data-testid={`import-job-${j.id}`}>
                  <td className="px-4 py-2 font-medium text-[#111827] truncate max-w-[180px]">{j.file_name}</td>
                  <td className="px-4 py-2"><StatusPill status={j.status} /></td>
                  <td className="px-4 py-2 text-[#6b7280]">{j.processed_rows}/{j.total_rows}</td>
                  <td className="px-4 py-2 text-[#166534]">{j.inserted_count}</td>
                  <td className="px-4 py-2 text-[#1d4ed8]">{j.updated_count}</td>
                  <td className="px-4 py-2 text-[#991b1b]">{j.error_count}</td>
                </tr>
              ))}
              {jobs.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-[#9ca3af]">No imports yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    completed: "bg-[#dcfce7] text-[#166534]", running: "bg-[#dbeafe] text-[#1d4ed8]",
    queued: "bg-[#fef3c7] text-[#92400e]", failed: "bg-[#fee2e2] text-[#991b1b]",
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${map[status] || "bg-[#f3f4f6] text-[#6b7280]"}`}>
      {["queued", "running"].includes(status) && <Loader2 className="h-3 w-3 animate-spin" />}{status}
    </span>
  );
}

/* ─────────────────────────── Pricing ─────────────────────────── */
function PricingTab({ headers }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [n, setN] = useState(14);
  const [q, setQ] = useState(null);
  const [waive, setWaive] = useState(false);
  const [waiveReason, setWaiveReason] = useState("");

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/admin/schools/pricing/config`, { headers }); setCfg(r.data); }
    catch { toast.error("Failed to load pricing"); }
  }, [headers]);
  useEffect(() => { load(); }, [load]);

  const preview = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/admin/schools/pricing/quote-preview`, { headers, params: { n, portfolio_license: true, waive_license: waive } }); setQ(r.data); }
    catch {}
  }, [headers, n, waive]);
  useEffect(() => { const t = setTimeout(preview, 250); return () => clearTimeout(t); }, [preview]);

  if (!cfg) return <div className="text-sm text-[#6b7280]">Loading pricing…</div>;

  const setTier = (i, rate) => setCfg((p) => {
    const tiers = p.tiers.map((t, ix) => ix === i ? { ...t, rate: Number(rate) || 0 } : t);
    return { ...p, tiers };
  });

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/admin/schools/pricing/config`,
        { tiers: cfg.tiers, portfolio_license_fee: cfg.portfolio_license_fee },
        { headers: { ...headers, "Content-Type": "application/json" } });
      setCfg(r.data); toast.success("Pricing saved"); preview();
    } catch { toast.error("Failed to save pricing"); }
    setSaving(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="schools-pricing">
      <div className="border border-[#e5e7eb] rounded-lg p-5 bg-white">
        <h3 className="font-semibold text-[#111827] mb-1">Bracket pricing (per school / year)</h3>
        <p className="text-xs text-[#6b7280] mb-4">Marginal brackets, like tax brackets. Each rate applies only to schools within that bracket.</p>
        <div className="space-y-2">
          {cfg.tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-sm text-[#374151] w-28">{t.tier_start}{t.tier_end ? `–${t.tier_end}` : "+"} schools</span>
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9ca3af]">$</span>
                <Input data-testid={`pricing-tier-${i}`} type="number" className="pl-6" value={t.rate}
                  onChange={(e) => setTier(i, e.target.value)} />
              </div>
            </div>
          ))}
          <div className="flex items-center gap-3 pt-2 border-t border-[#f1f2f4] mt-2">
            <span className="text-sm text-[#374151] w-28">Portfolio Manager License</span>
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9ca3af]">$</span>
              <Input data-testid="pricing-license-fee" type="number" className="pl-6" value={cfg.portfolio_license_fee}
                onChange={(e) => setCfg((p) => ({ ...p, portfolio_license_fee: Number(e.target.value) || 0 }))} />
            </div>
          </div>
        </div>
        <Button data-testid="pricing-save-btn" className="mt-4 bg-[#b91c1c] hover:bg-[#991b1b]" disabled={saving} onClick={save}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Pricing"}
        </Button>
        <p className="text-[11.5px] text-[#9ca3af] mt-2">Version {cfg.pricing_version}</p>
      </div>

      <div className="border border-[#e5e7eb] rounded-lg p-5 bg-[#fafafa]">
        <h3 className="font-semibold text-[#111827] mb-3">Quote preview</h3>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-sm text-[#374151]"># schools</span>
          <Input data-testid="pricing-preview-count" type="number" className="w-28" value={n} onChange={(e) => setN(Math.max(0, Number(e.target.value) || 0))} />
        </div>
        <div className="mb-4 rounded-md border border-[#e5e7eb] bg-white p-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" data-testid="pricing-waive-toggle" checked={waive} onChange={(e) => setWaive(e.target.checked)} className="h-4 w-4" />
            <span className="text-sm font-medium text-[#374151]">Waive Portfolio Manager License Fee</span>
          </label>
          {waive && (
            <Input data-testid="pricing-waive-reason" className="mt-2" placeholder="Waiver reason (required on quote)"
              value={waiveReason} onChange={(e) => setWaiveReason(e.target.value)} />
          )}
        </div>
        {q && (
          <div className="space-y-2 text-sm" data-testid="pricing-preview-result">
            {q.lines.map((l, i) => (
              <div key={i} className="flex justify-between text-[#374151]">
                <span>{l.quantity} × {fmt(l.rate)} <span className="text-[#9ca3af]">(bracket {l.tier_start}{l.tier_end ? `–${l.tier_end}` : "+"})</span></span>
                <span className="font-medium">{fmt(l.line_total)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t border-[#e5e7eb] pt-2 text-[#374151]" data-testid="pricing-preview-subtotal">
              <span>School license subtotal</span><span className="font-medium">{fmt(q.subtotal)}</span>
            </div>
            <div className="flex justify-between text-[#374151]">
              <span>{q.portfolio_license_display_name || "Portfolio Manager License"}</span><span className="font-medium">{fmt(q.portfolio_license_fee)}</span>
            </div>
            {q.license_waived && (
              <>
                <div className="flex justify-between text-[#b91c1c]" data-testid="pricing-preview-waiver">
                  <span>Waiver</span><span className="font-medium">({fmt(q.license_waiver_amount)})</span>
                </div>
                <div className="flex justify-between text-[#374151]">
                  <span>Net {q.portfolio_license_display_name || "Portfolio Manager License"}</span><span className="font-medium">{fmt(q.net_portfolio_license_fee)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between border-t border-[#111827]/10 pt-2 text-base font-bold text-[#111827]">
              <span>Annual total</span><span data-testid="pricing-preview-total">{fmt(q.annual_total)}</span>
            </div>
            <p className="text-[11.5px] text-[#9ca3af] pt-2">{cfg.tax_note || "State and local taxes are not included. Applicable taxes, if any, will be calculated at invoice time based on purchaser location and tax status."}</p>
          </div>
        )}
      </div>
    </div>
  );
}
